/* crm-email.jsx — Email foundation components for Phase 4
   Provides: EmailComposer, EmailThread, EmailTimeline,
             EmailTemplates, EmailMetrics, EmailInboxView
   All components self-register on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const _fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const _typeIcon = (type) => {
  if (!type) return 'Mail';
  const t = type.toUpperCase();
  if (t === 'INCOMING') return 'Inbox';
  if (t === 'OUTGOING' || t === 'SENT') return 'Send';
  if (t === 'DRAFT') return 'FileText';
  return 'Mail';
};

const _skeletonRow = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 0', borderBottom: '1px solid var(--nx-border)' }}>
    <div style={{ height: 10, width: '60%', background: 'var(--nx-border)', borderRadius: 4, animation: 'pulse 1.5s infinite' }} />
    <div style={{ height: 9, width: '40%', background: 'var(--nx-border)', borderRadius: 4, animation: 'pulse 1.5s infinite' }} />
  </div>
);

/* ══════════════════════════════════════════════════════════════
   EMAIL COMPOSER
   ══════════════════════════════════════════════════════════════ */
const EmailComposer = ({ record, onClose, onSent, template, replyTo }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const _getRecordEmail = () => {
    if (!record) return '';
    return record.email || record.primaryEmail || '';
  };

  const [to, setTo]           = useState(replyTo?.fromEmail || _getRecordEmail());
  const [subject, setSubject] = useState(replyTo ? ('Re: ' + (replyTo.subject || '')) : (template?.subject || ''));
  const [body, setBody]       = useState(template?.body || '');
  const [selTemplate, setSelTemplate] = useState(null);
  const [templates, setTemplates]     = useState([]);
  const [sending, setSending]         = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    window.__nx_api?.email?.listTemplates(wsId)
      .then(r => setTemplates(Array.isArray(r) ? r : (r?.data ?? [])))
      .catch(() => setTemplates([]));
  }, [wsId]);

  useEffect(() => {
    if (template) {
      setSubject(template.subject || '');
      setBody(template.body || '');
    }
  }, [template]);

  const _buildPayload = (status) => {
    const base = { toEmails: [to], subject, body, type: 'OUTGOING', status };
    if (record?.id) {
      if (record._type === 'person' || record.personId)   base.personId  = record.id || record.personId;
      if (record._type === 'company' || record.companyId) base.companyId = record.id || record.companyId;
      if (record._type === 'deal' || record.dealId)       base.dealId    = record.id || record.dealId;
    }
    return base;
  };

  const handleSend = async () => {
    if (!to.trim() || !subject.trim()) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'To and Subject are required' });
      return;
    }
    setSending(true);
    try {
      await window.__nx_api?.email?.createEmail(wsId, _buildPayload('SENT'));
      toast({ tone: 'success', icon: 'Check', title: 'Email sent' });
      if (typeof onSent === 'function') onSent();
      onClose();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await window.__nx_api?.email?.createEmail(wsId, _buildPayload('DRAFT'));
      toast({ tone: 'success', icon: 'Check', title: 'Draft saved' });
      onClose();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to save draft' });
    } finally {
      setSavingDraft(false);
    }
  };

  const handleApplyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setSelTemplate(tpl);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: 'var(--nx-surface)', borderRadius: 12, width: 560, maxWidth: '95vw', boxShadow: 'var(--nx-shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--nx-border)' }}>
          <NexusIcon name="Mail" size={16} style={{ color: 'var(--nx-accent)' }} />
          <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Compose Email</span>
          <IconBtn icon="X" onClick={onClose} title="Close" />
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', flex: 1 }}>
          {/* Template picker */}
          {templates.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NexusIcon name="FileText" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
              <select
                className="nx-select"
                style={{ flex: 1 }}
                value={selTemplate?.id || ''}
                onChange={e => {
                  const tpl = templates.find(t => t.id === e.target.value);
                  if (tpl) handleApplyTemplate(tpl);
                }}
              >
                <option value="">Use a template…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name || t.subject}</option>)}
              </select>
            </div>
          )}

          {/* To */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>To</label>
            <input
              className="nx-input"
              type="email"
              value={to}
              onChange={e => setTo(e.target.value)}
              placeholder="recipient@example.com"
              style={{ width: '100%' }}
            />
          </div>

          {/* Subject */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Subject</label>
            <input
              className="nx-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject"
              style={{ width: '100%' }}
            />
          </div>

          {/* Body */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Body</label>
            <textarea
              className="nx-input"
              rows={8}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Write your message…"
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--nx-border)', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={sending || savingDraft}>Cancel</Btn>
          <Btn variant="ghost" size="sm" onClick={handleSaveDraft} disabled={sending || savingDraft}>
            {savingDraft ? 'Saving…' : 'Save Draft'}
          </Btn>
          <Btn variant="primary" size="sm" onClick={handleSend} disabled={sending || savingDraft}>
            <NexusIcon name="Send" size={12} style={{ marginRight: 4 }} />
            {sending ? 'Sending…' : 'Send'}
          </Btn>
        </div>
      </div>
    </div>
  );
};
window.EmailComposer = EmailComposer;

/* ══════════════════════════════════════════════════════════════
   EMAIL THREAD
   ══════════════════════════════════════════════════════════════ */
const EmailThread = ({ emails = [], onReply }) => {
  const [expanded, setExpanded] = useState(new Set([emails[emails.length - 1]?.id].filter(Boolean)));

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!emails.length) return (
    <div className="nx-empty" style={{ padding: 'var(--space-4) 0' }}>
      <NexusIcon name="Mail" size={16} />
      <span>No emails in thread.</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {emails.map((email, idx) => {
        const isExpanded = expanded.has(email.id);
        const isLast = idx === emails.length - 1;
        return (
          <div
            key={email.id || idx}
            style={{
              border: '1px solid var(--nx-border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: isLast ? 'var(--nx-surface)' : 'var(--nx-surface-alt)',
            }}
          >
            {/* Card header */}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
              onClick={() => toggleExpand(email.id)}
            >
              <Avatar name={email.fromName || email.from || '?'} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {email.fromName || email.from || 'Unknown'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', flexShrink: 0 }}>{_fmtDate(email.sentAt || email.createdAt)}</span>
                </div>
                {!isExpanded && (
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {email.subject || '(no subject)'}
                  </div>
                )}
              </div>
              <NexusIcon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
            </div>

            {/* Expanded body */}
            {isExpanded && (
              <div style={{ padding: '0 14px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{email.subject || '(no subject)'}</div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
                  To: {Array.isArray(email.toEmails) ? email.toEmails.join(', ') : (email.to || '—')}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--nx-text)', marginTop: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {email.body || email.bodyPreview || '(empty)'}
                </div>
                {typeof onReply === 'function' && isLast && (
                  <div style={{ marginTop: 12 }}>
                    <Btn variant="ghost" size="sm" onClick={() => onReply(email)}>
                      <NexusIcon name="CornerUpLeft" size={12} style={{ marginRight: 4 }} />
                      Reply
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
window.EmailThread = EmailThread;

/* ══════════════════════════════════════════════════════════════
   EMAIL TIMELINE
   ══════════════════════════════════════════════════════════════ */
const EmailTimeline = ({ record, type }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [emails, setEmails]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [composing, setComposing] = useState(false);
  const [replyTo, setReplyTo]     = useState(null);

  const _buildParams = useCallback(() => {
    if (!record?.id) return {};
    const t = (type || record._type || '').toLowerCase();
    if (t === 'person' || t === 'people') return { personId: record.id };
    if (t === 'company' || t === 'companies') return { companyId: record.id };
    if (t === 'deal' || t === 'deals') return { dealId: record.id };
    return null;
  }, [record, type]);

  const load = useCallback(async () => {
    if (!wsId || !record?.id) return;
    const params = _buildParams();
    if (!params || Object.keys(params).length === 0) {
      setEmails([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await window.__nx_api?.email?.listEmails(wsId, params);
      setEmails(Array.isArray(res) ? res : (res?.data ?? []));
    } catch { setEmails([]); }
    finally { setLoading(false); }
  }, [wsId, record, _buildParams]);

  useEffect(() => { load(); }, [load]);

  /* Group by threadId */
  const threads = useMemo(() => {
    const map = new Map();
    emails.forEach(e => {
      const key = e.threadId || e.id;
      const arr = map.get(key) || [];
      arr.push(e);
      map.set(key, arr);
    });
    return Array.from(map.values()).sort((a, b) => {
      const ta = new Date(a[a.length - 1]?.sentAt || a[a.length - 1]?.createdAt || 0);
      const tb = new Date(b[b.length - 1]?.sentAt || b[b.length - 1]?.createdAt || 0);
      return tb - ta;
    });
  }, [emails]);

  const handleReply = (email) => {
    setReplyTo(email);
    setComposing(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn variant="primary" size="sm" onClick={() => { setReplyTo(null); setComposing(true); }}>
          <NexusIcon name="Plus" size={12} style={{ marginRight: 4 }} />
          Compose
        </Btn>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[1,2,3].map(i => <div key={i}>{_skeletonRow()}</div>)}
        </div>
      ) : threads.length === 0 ? (
        <div className="nx-empty" style={{ padding: 'var(--space-5) 0' }}>
          <NexusIcon name="Mail" size={20} />
          <span>No emails yet.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {threads.map((thread, i) => {
            const last = thread[thread.length - 1];
            const icon = _typeIcon(last?.type || last?.status);
            return (
              <div key={i} style={{ border: '1px solid var(--nx-border)', borderRadius: 8, overflow: 'hidden' }}>
                {/* Thread summary row */}
                <details>
                  <summary style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', listStyle: 'none', userSelect: 'none' }}>
                    <NexusIcon name={icon} size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {last?.subject || '(no subject)'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                        {last?.fromName || last?.from || '—'} · {_fmtDate(last?.sentAt || last?.createdAt)}
                        {thread.length > 1 && <span style={{ marginLeft: 6, color: 'var(--nx-accent)' }}>{thread.length} messages</span>}
                      </div>
                    </div>
                    <Pill tone={last?.type === 'INCOMING' ? 'blue' : last?.type === 'DRAFT' ? 'gray' : 'green'}>
                      {last?.type === 'INCOMING' ? 'Received' : last?.type === 'DRAFT' ? 'Draft' : 'Sent'}
                    </Pill>
                  </summary>
                  <div style={{ borderTop: '1px solid var(--nx-border)', padding: '12px 14px' }}>
                    <EmailThread emails={thread} onReply={handleReply} />
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}

      {composing && (
        <EmailComposer
          record={replyTo ? { ...record, email: Array.isArray(replyTo.toEmails) ? replyTo.toEmails[0] : replyTo.from } : record}
          onClose={() => { setComposing(false); setReplyTo(null); }}
          onSent={load}
          template={replyTo ? { subject: `Re: ${replyTo.subject || ''}` } : null}
        />
      )}
    </div>
  );
};
window.EmailTimeline = EmailTimeline;

/* ══════════════════════════════════════════════════════════════
   EMAIL TEMPLATES
   ══════════════════════════════════════════════════════════════ */
const EmailTemplates = ({ wsId: propWsId, onSelectTemplate }) => {
  const wsId = propWsId || window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [templates, setTemplates]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState({ name: '', subject: '', body: '' });
  const [saving, setSaving]           = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await window.__nx_api?.email?.listTemplates(wsId);
      setTemplates(Array.isArray(res) ? res : (res?.data ?? []));
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Name and Subject are required' });
      return;
    }
    setSaving(true);
    try {
      await window.__nx_api?.email?.createTemplate(wsId, form);
      toast({ tone: 'success', icon: 'Check', title: 'Template created' });
      setShowForm(false);
      setForm({ name: '', subject: '', body: '' });
      await load();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to create template' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (tpl) => {
    try {
      await window.__nx_api?.email?.removeTemplate(wsId, tpl.id);
      toast({ tone: 'success', icon: 'Check', title: 'Template deleted' });
      await load();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to delete template' });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Email Templates</span>
        <Btn variant="primary" size="sm" onClick={() => setShowForm(v => !v)}>
          <NexusIcon name="Plus" size={12} style={{ marginRight: 4 }} />
          New Template
        </Btn>
      </div>

      {showForm && (
        <div style={{ border: '1px solid var(--nx-accent)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--nx-surface-alt)' }}>
          <input
            className="nx-input"
            placeholder="Template name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ width: '100%' }}
          />
          <input
            className="nx-input"
            placeholder="Subject"
            value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            style={{ width: '100%' }}
          />
          <textarea
            className="nx-input"
            rows={4}
            placeholder="Body"
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" size="sm" onClick={() => { setShowForm(false); setForm({ name: '', subject: '', body: '' }); }}>Cancel</Btn>
            <Btn variant="primary" size="sm" onClick={handleCreate} disabled={saving}>{saving ? 'Saving…' : 'Save Template'}</Btn>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--nx-text-muted)', fontSize: 12 }}>Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="nx-empty" style={{ padding: 'var(--space-4) 0' }}>
          <NexusIcon name="FileText" size={16} />
          <span>No templates yet.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {templates.map(tpl => (
            <div
              key={tpl.id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--nx-border)', borderRadius: 8, background: 'var(--nx-surface)', cursor: 'pointer' }}
              onClick={() => typeof onSelectTemplate === 'function' && onSelectTemplate(tpl)}
            >
              <NexusIcon name="FileText" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{tpl.name}</div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.subject}</div>
              </div>
              <IconBtn
                icon="Trash"
                title="Delete template"
                style={{ color: 'var(--nx-danger)' }}
                onClick={e => { e.stopPropagation(); handleDelete(tpl); }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
window.EmailTemplates = EmailTemplates;

/* ══════════════════════════════════════════════════════════════
   EMAIL METRICS
   ══════════════════════════════════════════════════════════════ */
const EmailMetrics = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    window.__nx_api?.email?.getMetrics(wsId)
      .then(r => setMetrics(r))
      .catch(() => setMetrics(null))
      .finally(() => setLoading(false));
  }, [wsId]);

  const stats = useMemo(() => {
    if (metrics) {
      return [
        { label: 'Sent',     value: metrics.sent     ?? 0, icon: 'Send',     color: '#10B981' },
        { label: 'Received', value: metrics.received  ?? metrics.incoming ?? 0, icon: 'Inbox',    color: '#0EA5E9' },
        { label: 'Drafts',   value: metrics.drafts    ?? 0, icon: 'FileText', color: '#6366f1' },
      ];
    }
    return [
      { label: 'Sent',     value: '—', icon: 'Send',     color: '#10B981' },
      { label: 'Received', value: '—', icon: 'Inbox',    color: '#0EA5E9' },
      { label: 'Drafts',   value: '—', icon: 'FileText', color: '#6366f1' },
    ];
  }, [metrics]);

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {stats.map(s => (
        <div
          key={s.label}
          style={{ flex: '1 1 100px', minWidth: 100, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon name={s.icon} size={14} style={{ color: s.color }} />
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>{s.label}</span>
          </div>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--nx-text)', lineHeight: 1 }}>
            {loading ? '…' : (typeof s.value === 'number' ? s.value.toLocaleString() : s.value)}
          </span>
        </div>
      ))}
    </div>
  );
};
window.EmailMetrics = EmailMetrics;

/* ══════════════════════════════════════════════════════════════
   EMAIL INBOX VIEW
   ══════════════════════════════════════════════════════════════ */
const EmailInboxView = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const toast = useToast();

  const [tab, setTab]               = useState('all');
  const [emails, setEmails]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [composing, setComposing]   = useState(false);
  const [replyTo, setReplyTo]       = useState(null);
  const [openThread, setOpenThread] = useState(null);

  const TAB_FILTER = { all: null, inbox: 'INCOMING', sent: 'OUTGOING', drafts: 'DRAFT' };

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = {};
      if (TAB_FILTER[tab]) params.type = TAB_FILTER[tab];
      const res = await window.__nx_api?.email?.listEmails(wsId, params);
      setEmails(Array.isArray(res) ? res : (res?.data ?? []));
    } catch { setEmails([]); }
    finally { setLoading(false); }
  }, [wsId, tab]);

  useEffect(() => { load(); }, [load]);

  /* Group by thread for inbox display */
  const threads = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = emails.filter(e =>
      !q || (e.subject || '').toLowerCase().includes(q) ||
      (e.fromName || e.from || '').toLowerCase().includes(q) ||
      (e.body || '').toLowerCase().includes(q)
    );
    const map = new Map();
    filtered.forEach(e => {
      const key = e.threadId || e.id;
      const arr = map.get(key) || [];
      arr.push(e);
      map.set(key, arr);
    });
    return Array.from(map.values()).sort((a, b) => {
      const ta = new Date(a[a.length - 1]?.sentAt || a[a.length - 1]?.createdAt || 0);
      const tb = new Date(b[b.length - 1]?.sentAt || b[b.length - 1]?.createdAt || 0);
      return tb - ta;
    });
  }, [emails, search]);

  const TABS = [
    { key: 'all',    label: 'All',    icon: 'Mail' },
    { key: 'inbox',  label: 'Inbox',  icon: 'Inbox' },
    { key: 'sent',   label: 'Sent',   icon: 'Send' },
    { key: 'drafts', label: 'Drafts', icon: 'FileText' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--nx-border)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '6px 10px', flex: 1, minWidth: 160, gap: 6 }}>
          <NexusIcon name="Search" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
          <input
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, flex: 1, color: 'var(--nx-text)' }}
            placeholder="Search emails…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Btn variant="primary" size="sm" onClick={() => setComposing(true)}>
          <NexusIcon name="Plus" size={12} style={{ marginRight: 4 }} />
          Compose
        </Btn>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', padding: '0 16px' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '10px 14px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--nx-accent)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            <NexusIcon name={t.icon} size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Email list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '0 16px' }}>
            {[1,2,3,4].map(i => <div key={i}>{_skeletonRow()}</div>)}
          </div>
        ) : threads.length === 0 ? (
          <div className="nx-empty" style={{ padding: 'var(--space-6) 0', margin: 'auto' }}>
            <NexusIcon name="Inbox" size={24} />
            <span>No emails found.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {threads.map((thread, i) => {
              const last = thread[thread.length - 1];
              const isOpen = openThread === i;
              const icon = _typeIcon(last?.type || last?.status);
              return (
                <div key={i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', background: isOpen ? 'var(--nx-surface-alt)' : 'transparent' }}
                    onClick={() => setOpenThread(isOpen ? null : i)}
                  >
                    <NexusIcon name={icon} size={15} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                    <Avatar name={last?.fromName || last?.from || '?'} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {last?.fromName || last?.from || '—'}
                        </span>
                        {thread.length > 1 && (
                          <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', background: 'var(--nx-border)', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                            {thread.length}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--nx-text)' }}>
                        {last?.subject || '(no subject)'}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', flexShrink: 0 }}>{_fmtDate(last?.sentAt || last?.createdAt)}</span>
                  </div>
                  {isOpen && (
                    <div style={{ padding: '0 16px 14px 56px', borderTop: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)' }}>
                      <EmailThread emails={thread} onReply={(email) => { setReplyTo(email); setComposing(true); }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {composing && (
        <EmailComposer
          record={null}
          replyTo={replyTo}
          onClose={() => { setComposing(false); setReplyTo(null); }}
          onSent={() => { setComposing(false); setReplyTo(null); load(); }}
        />
      )}
    </div>
  );
};
window.EmailInboxView = EmailInboxView;
